<!-- Bakta 序列分析组件 -->
<template>
  <div class="bakta-container">
    <div class="content-section">
      <h1>Genome Annotation (Bakta)</h1>
      
      <div class="description">
        <h2>About Bakta</h2>
        <p>Bakta is a tool for the rapid and standardized annotation of bacterial genomes and plasmids. It provides a complete annotation workflow including the prediction of small RNAs and CRISPR arrays.</p>
        
        <p class="additional-info">
          Bakta combines ab initio predictions with database-based approaches to provide comprehensive genome annotations. It generates standardized, high-quality annotations that are consistent with INSDC submission requirements.
        </p>
        <ul class="feature-list">
          <li><strong>Comprehensive:</strong> Annotates protein coding sequences, RNA genes, CRISPR arrays, and more</li>
          <li><strong>Fast:</strong> Optimized for rapid analysis of bacterial genomes</li>
        </ul>
        
        <el-alert
          title="Processing Time Note"
          type="info"
          :closable="false"
          show-icon
        >
          <p>Analysis may take several minutes depending on genome size. Please be patient.</p>
        </el-alert>

        <div class="features">
          <h3>Key Features:</h3>
          <ul>
            <li>Protein coding sequences (CDS) prediction</li>
            <li>tRNA and rRNA gene detection</li>
            <li>CRISPR array identification</li>
            <li>GC content calculation</li>
            <li>Coding density analysis</li>
            <li>Standardized output formats (GFF3, GenBank, etc.)</li>
          </ul>
        </div>
      </div>

      <div class="upload-section">
        <h2>Upload Genome File</h2>
        <p class="upload-info">Please upload a genome file in FASTA format (.fa, .fasta, .fna). Maximum file size: 50MB.</p>
        
        <el-upload
          class="upload-demo"
          drag
          :action="uploadUrl"
          :headers="headers"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
          :before-upload="beforeUpload"
          :on-progress="handleProgress"
          :multiple="false"
          :limit="1"
          :auto-upload="true"
          accept=".fasta,.fa,.fna"
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            Drop file here or <em>click to upload</em>
          </div>
          <template #tip>
            <div class="el-upload__tip">
              Supported file types: .fa, .fasta, .fna (max 50MB)
            </div>
          </template>
        </el-upload>

        <!-- Parameters Configuration -->
        <div class="parameters-section">
          <h3>Analysis Parameters</h3>
          <el-tabs type="border-card">
            <!-- 分析类型选择 -->
            <el-tab-pane label="Analysis Type">
              <el-form-item label="Analysis Type" required>
                <el-select v-model="parameters.analysisType" placeholder="Select analysis type">
                  <el-option
                    v-for="option in analysisTypeOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  />
                </el-select>
              </el-form-item>
            </el-tab-pane>

            <!-- 生物体信息 -->
            <el-tab-pane label="Organism">
              <div class="parameter-note">All fields in this section are optional</div>
              <el-form :model="parameters.organismParams" label-width="150px">
                <el-form-item label="Genus">
                  <el-input v-model="parameters.organismParams.genus" placeholder="Optional: Enter genus name" />
                </el-form-item>
                <el-form-item label="Species">
                  <el-input v-model="parameters.organismParams.species" placeholder="Optional: Enter species name" />
                </el-form-item>
                <el-form-item label="Strain">
                  <el-input v-model="parameters.organismParams.strain" placeholder="Optional: Enter strain name" />
                </el-form-item>
                <el-form-item label="Locus Prefix">
                  <el-input v-model="parameters.organismParams.locusPrefix" placeholder="Optional: Enter locus prefix" />
                </el-form-item>
                <el-form-item label="Locus Tag Prefix">
                  <el-input v-model="parameters.organismParams.locusTagPrefix" placeholder="Optional: Enter locus tag prefix" />
                </el-form-item>
              </el-form>
            </el-tab-pane>

            <!-- 注释设置 -->
            <el-tab-pane label="Annotation">
              <div class="parameter-note">Fields marked with * are required</div>
              <el-form :model="parameters.annotationParams" label-width="180px">
                <el-form-item label="Complete Genome">
                  <el-switch v-model="parameters.annotationParams.completeGenome" />
                  <span class="parameter-tip">Optional: Set if input is a complete genome</span>
                </el-form-item>
                <el-form-item label="Keep Contig Headers">
                  <el-switch v-model="parameters.annotationParams.keepContigHeaders" />
                  <span class="parameter-tip">Optional: Preserve original contig headers</span>
                </el-form-item>
                <el-form-item label="INSDC Compliant">
                  <el-switch v-model="parameters.annotationParams.insdcCompliant" />
                  <span class="parameter-tip">Optional: Generate INSDC compliant output</span>
                </el-form-item>
                <el-form-item label="Min Contig Length" required>
                  <el-input-number 
                    v-model="parameters.annotationParams.minContigLength"
                    :min="1"
                    :step="1"
                    :precision="0"
                    :controls="true"
                    :controls-position="'right'"
                  />
                  <span class="parameter-tip">Required: Minimum contig length to analyze</span>
                </el-form-item>
                <el-form-item label="Translation Table" required>
                  <el-select v-model="parameters.annotationParams.translationTable" placeholder="Select translation table">
                    <el-option
                      v-for="option in translationTableOptions"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                    />
                  </el-select>
                  <span class="parameter-tip">Required: Genetic code for translation</span>
                </el-form-item>
                <el-form-item label="Cell Wall Type">
                  <el-select v-model="parameters.annotationParams.cellWallType" placeholder="Select cell wall type">
                    <el-option
                      v-for="option in cellWallTypeOptions"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                    />
                  </el-select>
                  <span class="parameter-tip">Optional: Specify cell wall type for better predictions</span>
                </el-form-item>
                <el-form-item label="Prodigal Training File">
                  <el-upload
                    class="upload-demo"
                    action="#"
                    :auto-upload="false"
                    :on-change="handleProdigalFileChange"
                    :limit="1"
                  >
                    <el-button type="primary">Select File</el-button>
                  </el-upload>
                  <span class="parameter-tip">Optional: Custom Prodigal training file</span>
                </el-form-item>
              </el-form>
            </el-tab-pane>

            <!-- 复制子信息 -->
            <el-tab-pane label="Replicons">
              <div class="parameter-note">All replicon information is optional</div>
              <div class="replicon-controls">
                <el-button type="primary" @click="addRepliconSequence">Add Sequence</el-button>
              </div>
              <div v-for="(sequence, index) in parameters.repliconParams.sequences" :key="index" class="replicon-sequence">
                <el-form :model="sequence" label-width="120px">
                  <el-form-item label="Original ID">
                    <el-input v-model="sequence.originalId" />
                  </el-form-item>
                  <el-form-item label="Length">
                    <el-input-number v-model="sequence.length" :min="0" />
                  </el-form-item>
                  <el-form-item label="New ID">
                    <el-input v-model="sequence.newId" />
                  </el-form-item>
                  <el-form-item label="Type">
                    <el-select v-model="sequence.type">
                      <el-option
                        v-for="option in sequenceTypeOptions"
                        :key="option.value"
                        :label="option.label"
                        :value="option.value"
                      />
                    </el-select>
                  </el-form-item>
                  <el-form-item label="Topology">
                    <el-select v-model="sequence.topology">
                      <el-option
                        v-for="option in topologyOptions"
                        :key="option.value"
                        :label="option.label"
                        :value="option.value"
                      />
                    </el-select>
                  </el-form-item>
                  <el-form-item label="Name">
                    <el-input v-model="sequence.name" />
                  </el-form-item>
                  <el-button type="danger" @click="removeRepliconSequence(index)">Remove</el-button>
                </el-form>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>

        <!-- Start Analysis Button -->
        <div class="analysis-button-section">
          <el-button 
            type="primary" 
            :loading="analyzing"
            :disabled="!uploadedFile"
            @click="startAnalysis"
          >
            {{ uploadedFile ? 'Start Analysis' : 'Start Analysis' }}
          </el-button>
        </div>

        <!-- Upload Progress Display -->
        <div v-if="uploading" class="progress-section">
          <el-progress 
            :percentage="uploadProgress" 
            :status="uploadProgress === 100 ? 'success' : ''"
            :format="progressFormat"
          />
          <p class="progress-text">{{ progressText }}</p>
        </div>
      </div>

      <!-- Results Display Section -->
      <div v-if="analysisResults" class="results-section">
        <div class="results-header">
          <h2>Analysis Results</h2>
        </div>

        <el-tabs type="border-card" class="results-tabs">
          <!-- Job Statistics Tab -->
          <el-tab-pane label="Job statistics">
            <div class="statistics-section">
              <el-row :gutter="20">
                <!-- Input Information -->
                <el-col :span="12">
                  <div class="stat-group">
                    <h3>Input</h3>
                    <el-descriptions :column="1" border>
                      <el-descriptions-item label="Organism">
                        {{ parameters.organismParams.genus }} {{ parameters.organismParams.species || 'N.A.' }}
                      </el-descriptions-item>
                      <el-descriptions-item label="Sequences">
                        {{ analysisResults.contigs }} {{ analysisResults.contigs === 1 ? 'complete sequence' : 'sequences' }}
                      </el-descriptions-item>
                      <el-descriptions-item label="Genome size">
                        {{ formatNumber(analysisResults.genome_size) }} bp
                      </el-descriptions-item>
                    </el-descriptions>
                  </div>
                </el-col>

                <!-- Statistics -->
                <el-col :span="12">
                  <div class="stat-group">
                    <h3>Statistics</h3>
                    <el-descriptions :column="1" border>
                      <el-descriptions-item label="N50">
                        {{ formatNumber(analysisResults.genome_size) }} bp
                      </el-descriptions-item>
                      <el-descriptions-item label="GC-content">
                        {{ formatPercent(analysisResults.gc_content) }}
                      </el-descriptions-item>
                      <el-descriptions-item label="Coding ratio">
                        {{ formatPercent(analysisResults.coding_density) }}
                      </el-descriptions-item>
                    </el-descriptions>
                  </div>
                </el-col>
              </el-row>

              <!-- Feature Counts -->
              <div class="stat-group">
                <h3>Feature counts (Total: {{ getTotalFeatures() }})</h3>
                <el-row :gutter="20">
                  <el-col :span="8">
                    <el-descriptions :column="1" border>
                      <el-descriptions-item label="tRNA">{{ analysisResults.genes.trna || 0 }}</el-descriptions-item>
                      <el-descriptions-item label="tmRNA">{{ analysisResults.genes.tmrna || 0 }}</el-descriptions-item>
                      <el-descriptions-item label="rRNA">{{ analysisResults.genes.rrna || 0 }}</el-descriptions-item>
                      <el-descriptions-item label="ncRNA">{{ analysisResults.genes.ncrna || 0 }}</el-descriptions-item>
                    </el-descriptions>
                  </el-col>
                  <el-col :span="8">
                    <el-descriptions :column="1" border>
                      <el-descriptions-item label="ncRNA regions">{{ analysisResults.genes.ncrna_regions || 0 }}</el-descriptions-item>
                      <el-descriptions-item label="CRISPR">{{ analysisResults.genes.crispr || 0 }}</el-descriptions-item>
                      <el-descriptions-item label="CDS">{{ analysisResults.genes.total_cds || 0 }}</el-descriptions-item>
                      <el-descriptions-item label="sORF">{{ analysisResults.genes.sorf || 0 }}</el-descriptions-item>
                    </el-descriptions>
                  </el-col>
                  <el-col :span="8">
                    <el-descriptions :column="1" border>
                      <el-descriptions-item label="oriC">{{ analysisResults.genes.oric || 0 }}</el-descriptions-item>
                      <el-descriptions-item label="oriV">{{ analysisResults.genes.oriv || 0 }}</el-descriptions-item>
                      <el-descriptions-item label="oriT">{{ analysisResults.genes.orit || 0 }}</el-descriptions-item>
                      <el-descriptions-item label="gap">{{ analysisResults.genes.gap || 0 }}</el-descriptions-item>
                    </el-descriptions>
                  </el-col>
                </el-row>
              </div>
            </div>
          </el-tab-pane>

          <!-- Annotation Table Tab -->
          <el-tab-pane label="Annotation table">
            <div class="annotation-table">
              <div class="table-controls">
                <div class="entries-control">
                  <span>Show</span>
                  <el-select v-model="pageSize" size="small" @change="handlePageSizeChange">
                    <el-option :value="10" label="10" />
                    <el-option :value="25" label="25" />
                    <el-option :value="50" label="50" />
                    <el-option :value="100" label="100" />
                  </el-select>
                  <span>entries</span>
                </div>
                <div class="search-control">
                  <span>Search:</span>
                  <el-input
                    v-model="searchQuery"
                    size="small"
                    placeholder="Search in results..."
                    @input="handleSearch"
                  />
                </div>
              </div>

              <el-table
                :data="filteredTableData"
                style="width: 100%"
                border
                :max-height="500"
                v-loading="loading"
                :error="false"
                @error="handleTableError"
              >
                <el-table-column prop="sequence" label="Sequence" min-width="120" />
                <el-table-column prop="type" label="Type" min-width="100" sortable />
                <el-table-column prop="start" label="Start" min-width="100" sortable />
                <el-table-column prop="end" label="Stop" min-width="100" sortable />
                <el-table-column prop="strand" label="Strand" width="80" align="center" />
                <el-table-column prop="locus_tag" label="Locus tag" min-width="120" />
                <el-table-column prop="product" label="Product" min-width="200" show-overflow-tooltip />
                <el-table-column 
                  prop="dbxrefs" 
                  label="DBxrefs" 
                  min-width="150"
                  show-overflow-tooltip
                >
                  <template #default="{ row }">
                    <template v-if="row.dbxrefs && row.dbxrefs.length">
                      <el-link 
                        v-for="(ref, index) in formatDbxrefs(row).split(', ')"
                        :key="index"
                        type="primary"
                        :href="getDbxrefLink(ref)"
                        target="_blank"
                        class="dbxref-link"
                      >
                        {{ ref }}
                      </el-link>
                    </template>
                  </template>
                </el-table-column>
              </el-table>

              <div class="table-footer">
                <div class="entries-info">
                  Showing {{ paginationInfo.from }} to {{ paginationInfo.to }} of {{ paginationInfo.total }} entries
                  <span v-if="searchQuery">(filtered from {{ totalFilteredData }} total entries)</span>
                </div>
                <el-pagination
                  v-model:current-page="currentPage"
                  :page-size="pageSize"
                  :total="totalFilteredData"
                  :page-sizes="[10, 25, 50, 100]"
                  layout="total, sizes, prev, pager, next, jumper"
                  background
                  @size-change="handlePageSizeChange"
                  @current-change="handleCurrentPageChange"
                />
              </div>
            </div>
          </el-tab-pane>

          <!-- Circular Plot Tab -->
          <el-tab-pane label="Circular plot">
            <div class="circular-plot">
              <template v-if="analysisResults && analysisResults.files && analysisResults.files.svg">
                <div class="plot-container">
                  <img 
                    :src="`/api/tools/bakta/download/${currentJobId}/${analysisResults.files.svg}`"
                    alt="Circular genome plot"
                    class="genome-plot"
                    @error="handleImageError"
                    @load="handleImageLoad"
                  />
                </div>
              </template>
              <div v-else class="no-plot-message">
                {{ getPlotMessage() }}
              </div>
            </div>
          </el-tab-pane>

          <!-- Downloads Tab -->
          <el-tab-pane label="Downloads">
            <div class="downloads-section">
              <el-table 
                :data="getDownloadFiles()" 
                style="width: 100%" 
                border
                v-loading="loading"
                :error="false"
                @error="handleTableError"
              >
                <el-table-column prop="name" label="File" show-overflow-tooltip />
                <el-table-column prop="description" label="Description" show-overflow-tooltip />
                <el-table-column label="Action" width="120" align="center" fixed="right">
                  <template #default="scope">
                    <el-button
                      type="primary"
                      size="small"
                      @click="downloadFile(scope.row.name)"
                    >
                      Download
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>

    <!-- Citation Section -->
    <div class="citation-section">
      <h3>Citation and License</h3>
      <div class="citation-content">
        <p class="citation-text">
          This tool integrates Bakta. If you use this tool in your research, please cite:
        </p>
        <div class="citation-box">
          <p class="paper-citation">
            <strong>Primary Citation:</strong><br>
            Schwengers O, Jelonek L, Dieckmann MA, Beyvers S, Blom J, Goesmann A. (2021).
            Bakta: rapid and standardized annotation of bacterial genomes via alignment-free sequence identification.
            <em>Microbial Genomics</em>, 7(11):000685.
            <a href="https://doi.org/10.1099/mgen.0.000685" target="_blank">https://doi.org/10.1099/mgen.0.000685</a>
          </p>
          <p class="repo-citation">
            <strong>Software Repository:</strong><br>
            Bakta GitHub Repository: <a href="https://github.com/oschwengers/bakta" target="_blank">https://github.com/oschwengers/bakta</a>
          </p>
        </div>
        <div class="license-info">
          <p class="license-text">
            Bakta is licensed under the GNU General Public License v3.0 (GPL-3.0).
          </p>
          <p class="usage-note">
            This is an integration of Bakta and is not officially affiliated with the Bakta project. For the official implementation and latest updates, please visit the Bakta GitHub repository.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { UploadFilled } from '@element-plus/icons-vue'
import axios from 'axios'

export default {
  name: 'BaktaAnalyzer',
  components: {
    UploadFilled
  },
  setup() {
    const analysisResults = ref(null)
    const currentJobId = ref(null)
    const uploading = ref(false)
    const uploadProgress = ref(0)
    const progressText = ref('')
    const uploadedFile = ref(null)
    const analyzing = ref(false)
    const loading = ref(false)

    // 文件上传URL
    const uploadUrl = ref('/api/tools/bakta/upload/')

    // 添加上传请求头
    const headers = {
      'X-CSRFToken': document.cookie.match(/csrftoken=([\w-]+)/)?.[1] || ''
    }

    // 格式化数字
    const formatNumber = (num) => {
      return num ? num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : '0'
    }

    // 格式化百分比
    const formatPercent = (value) => {
      return value !== undefined && value !== null ? `${value.toFixed(2)}%` : '0.00%'
    }

    // 获取GC含量颜色
    const getGCColor = (value) => {
      value = Number(value)
      if (value >= 60) return '#67C23A'
      if (value >= 40) return '#E6A23C'
      return '#F56C6C'
    }

    // 获取编码密度颜色
    const getCodingDensityColor = (value) => {
      value = Number(value)
      if (value >= 85) return '#67C23A'
      if (value >= 70) return '#E6A23C'
      return '#F56C6C'
    }

    // 进度格式化
    const progressFormat = (percentage) => {
      if (percentage === 100) {
        return 'Processing'
      }
      return `${percentage}%`
    }

    // 处理上传进度
    const handleProgress = (event) => {
      uploadProgress.value = Math.round(event.percent)
      if (uploadProgress.value < 100) {
        progressText.value = `Upload progress: ${uploadProgress.value}%`
      } else {
        progressText.value = 'Analyzing genome...'
      }
    }

    // 注释表格数据
    const annotationTableData = ref([])

    // 获取文件描述
    const getFileDescription = (filename) => {
      const descriptions = {
        'tsv': 'Tab-separated annotation summary',
        'gbff': 'GenBank flat file format',
        'gff3': 'General Feature Format v3',
        'svg': 'Circular genome visualization',
        'faa': 'Amino acid sequences (FASTA)',
        'ffn': 'Feature nucleotide sequences (FASTA)',
        'log': 'Analysis log file'
      }
      const ext = filename.split('.').pop()
      return descriptions[ext] || 'Result file'
    }

    // 获取下载文件列表
    const getDownloadFiles = () => {
      if (!analysisResults.value || !analysisResults.value.files) return []
      
      return Object.entries(analysisResults.value.files)
        .filter(entry => entry[1] !== null)
        .map(entry => ({
          name: entry[1],
          description: getFileDescription(entry[0]),
          type: entry[0]
        }))
    }

    // 下载文件
    const downloadFile = async (filename) => {
      if (!currentJobId.value) {
        ElMessage.error('Cannot get task ID')
        return
      }

      try {
        const response = await fetch(`/api/tools/bakta/download/${currentJobId.value}/${filename}`)
        if (!response.ok) throw new Error('Download failed')

        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)

        ElMessage.success('File downloaded successfully')
      } catch (error) {
        console.error('Download failed:', error)
        ElMessage.error('Download failed')
      }
    }

    // 处理上传成功
    const handleUploadSuccess = (response) => {
      uploading.value = false
      if (response.status === 'success') {
        ElMessage.success('文件上传成功')
        uploadedFile.value = response.file_id

        // 如果有序列信息，自动添加复制子信息
        if (response.sequence_info) {
          // 清空现有序列
          parameters.value.repliconParams.sequences = []
          
          // 添加新的序列信息
          parameters.value.repliconParams.sequences.push({
            originalId: response.sequence_info.id,
            length: response.sequence_info.length,
            newId: '',  // 可选，让用户填写
            type: parameters.value.analysisType === 'plasmid' ? 'plasmid' : 'contig',  // 根据分析类型设置默认值
            topology: parameters.value.analysisType === 'plasmid' ? 'circular' : 'linear',  // 质粒默认为环状
            name: ''  // 可选，让用户填写
          })

          console.log('自动填充的复制子信息：', parameters.value.repliconParams.sequences)
        }
      } else {
        console.error('上传失败:', response.message)
        ElMessage.error(response.message || '上传失败')
      }
    }

    // 上传错误处理
    const handleUploadError = (error) => {
      uploading.value = false
      console.error('Upload failed:', error)
      ElMessage.error('File upload failed')
    }

    // 上传前验证
    const beforeUpload = (file) => {
      const validTypes = ['.fasta', '.fa', '.fna']
      const isValidType = validTypes.some(type => 
        file.name.toLowerCase().endsWith(type)
      )
      
      if (!isValidType) {
        ElMessage.error('Please upload a FASTA format file')
        return false
      }

      const maxSize = 50 * 1024 * 1024 // 50MB
      if (file.size > maxSize) {
        ElMessage.error('File size cannot exceed 50MB')
        return false
      }

      uploading.value = true
      uploadProgress.value = 0
      progressText.value = 'Preparing upload...'
      return true
    }

    // 开始分析
    const startAnalysis = async () => {
      if (!uploadedFile.value) {
        ElMessage.warning('请先上传文件')
        return
      }

      analyzing.value = true
      try {
        const formData = new FormData()
        formData.append('file_id', uploadedFile.value)
        formData.append('parameters', JSON.stringify(parameters.value))

        console.log('开始分析，参数：', parameters.value)
        
        const response = await axios.post('/api/tools/bakta/analyze/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-CSRFToken': headers['X-CSRFToken']
          }
        })

        console.log('分析响应：', response.data)

        if (response.data.status === 'success') {
          // 先清除旧的结果
          currentJobId.value = response.data.job_id
          analysisResults.value = response.data.results
          annotationTableData.value = response.data.results.annotation_data || []

          console.log('设置任务ID：', currentJobId.value)
          console.log('分析结果：', analysisResults.value)
          
          if (analysisResults.value.files?.svg) {
            console.log('环状图文件：', analysisResults.value.files.svg)
            console.log('环状图URL：', `/api/tools/bakta/download/${currentJobId.value}/${analysisResults.value.files.svg}`)
          } else {
            console.warn('未找到环状图文件')
          }

          ElMessage.success('分析完成')
        } else {
          throw new Error(response.data.message || '分析失败')
        }
      } catch (error) {
        console.error('分析错误：', error)
        ElMessage.error(error.message || '分析失败')
      } finally {
        analyzing.value = false
      }
    }

    const parameters = ref({
      analysisType: 'genome',
      organismParams: {
        genus: '',
        species: '',
        strain: '',
        locusPrefix: '',
        locusTagPrefix: ''
      },
      annotationParams: {
        completeGenome: false,
        keepContigHeaders: false,
        insdcCompliant: true,
        minContigLength: 1,
        translationTable: 11,
        cellWallType: 'auto',
        prodigalTrainingFile: null
      },
      repliconParams: {
        sequences: []
      }
    })

    // 分析类型选项
    const analysisTypeOptions = [
      { value: 'genome', label: 'Genome Analysis' },
      { value: 'plasmid', label: 'Plasmid Analysis' },
      { value: 'metagenome', label: 'Metagenome Assembly Analysis' }
    ]

    // 翻译表选项
    const translationTableOptions = [
      { value: 11, label: 'Table 11 (Bacteria, Archaea)' },
      { value: 4, label: 'Table 4 (Mycoplasma, Spiroplasma)' }
    ]

    // 细胞壁类型选项
    const cellWallTypeOptions = [
      { value: 'auto', label: 'Auto' },
      { value: 'gram+', label: 'Gram Positive' },
      { value: 'gram-', label: 'Gram Negative' },
      { value: 'diderm', label: 'Diderm' },
      { value: 'monoderm', label: 'Monoderm' },
      { value: 'archaea', label: 'Archaea' }
    ]

    // 序列类型选项
    const sequenceTypeOptions = [
      { value: 'chromosome', label: 'Chromosome' },
      { value: 'plasmid', label: 'Plasmid' },
      { value: 'contig', label: 'Contig' }
    ]

    // 拓扑结构选项
    const topologyOptions = [
      { value: 'circular', label: 'Circular' },
      { value: 'linear', label: 'Linear' }
    ]

    // 处理复制子文件变化
    const handleProdigalFileChange = (file) => {
      if (file) {
        parameters.value.annotationParams.prodigalTrainingFile = file.raw
      }
    }

    // 添加复制子序列
    const addRepliconSequence = () => {
      parameters.value.repliconParams.sequences.push({
        originalId: '',
        length: 0,
        newId: '',
        type: 'contig',
        topology: 'linear',
        name: ''
      })
    }

    // 删除复制子序列
    const removeRepliconSequence = (index) => {
      parameters.value.repliconParams.sequences.splice(index, 1)
    }

    // Add these methods in the setup function
    const getTotalFeatures = () => {
      if (!analysisResults.value || !analysisResults.value.genes) return 0;
      const genes = analysisResults.value.genes;
      return (
        (genes.trna || 0) +
        (genes.tmrna || 0) +
        (genes.rrna || 0) +
        (genes.ncrna || 0) +
        (genes.ncrna_regions || 0) +
        (genes.crispr || 0) +
        (genes.total_cds || 0) +
        (genes.sorf || 0) +
        (genes.oric || 0) +
        (genes.oriv || 0) +
        (genes.orit || 0) +
        (genes.gap || 0)
      );
    };

    const formatDuration = (seconds) => {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes} minutes, ${remainingSeconds} seconds`;
    };

    // 处理表格错误
    const handleTableError = (error) => {
      console.warn('Table resize error:', error)
      // 忽略 ResizeObserver 错误
      if (error.message && error.message.includes('ResizeObserver')) {
        return
      }
      ElMessage.error('Error loading table data')
    }

    // 格式化 DBxrefs 字段
    const formatDbxrefs = (row) => {
      if (Array.isArray(row.dbxrefs)) {
        return row.dbxrefs.join(', ')
      }
      return row.dbxrefs || ''
    }

    // 添加分页和搜索相关的响应式变量
    const pageSize = ref(10)
    const currentPage = ref(1)
    const searchQuery = ref('')

    // 处理表格数据过滤和分页
    const filteredTableData = computed(() => {
      // 首先过滤掉不需要的数据
      let data = annotationTableData.value.filter(item => item.type !== 'gene')
      
      // 搜索过滤
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        data = data.filter(item => {
          return Object.values(item).some(val => 
            val && val.toString().toLowerCase().includes(query)
          )
        })
      }
      
      // 分页
      const start = (currentPage.value - 1) * pageSize.value
      const end = start + pageSize.value
      return data.slice(start, end)
    })

    // 过滤后的总数据量
    const totalFilteredData = computed(() => {
      let data = annotationTableData.value.filter(item => item.type !== 'gene')
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        data = data.filter(item => {
          return Object.values(item).some(val => 
            val && val.toString().toLowerCase().includes(query)
          )
        })
      }
      return data.length
    })

    // 分页信息计算
    const paginationInfo = computed(() => {
      const total = totalFilteredData.value
      const from = Math.min((currentPage.value - 1) * pageSize.value + 1, total)
      const to = Math.min(currentPage.value * pageSize.value, total)
      return { from, to, total }
    })

    // 处理页码变化
    const handleCurrentPageChange = (val) => {
      currentPage.value = val
    }

    // 处理每页显示数量变化
    const handlePageSizeChange = (val) => {
      pageSize.value = val
      currentPage.value = 1
    }

    // 处理搜索
    const handleSearch = () => {
      currentPage.value = 1
    }

    // 获取数据库引用链接
    const getDbxrefLink = (ref) => {
      const [db, id] = ref.split(':')
      const dbLinks = {
        'PFAM': `http://pfam.xfam.org/family/${id}`,
        'GO': `http://amigo.geneontology.org/amigo/term/${id}`,
        'KEGG': `https://www.genome.jp/dbget-bin/www_bget?${id}`,
        // 可以添加更多数据库的链接模式
      }
      return dbLinks[db] || '#'
    }

    // 在组件挂载时添加全局错误处理
    onMounted(() => {
      window.addEventListener('error', (event) => {
        if (event.message && event.message.includes('ResizeObserver')) {
          event.stopPropagation()
          event.preventDefault()
        }
      })
    })

    // 在组件卸载时移除错误处理
    onUnmounted(() => {
      window.removeEventListener('error', () => {})
    })

    // 在 setup() 函数中添加新的方法
    const getPlotMessage = () => {
      if (!analysisResults.value) {
        return '请先运行分析'
      }
      if (!analysisResults.value.files) {
        return '分析结果中没有文件信息'
      }
      if (!analysisResults.value.files.svg) {
        return '没有找到环状图文件'
      }
      return '无法加载环状图'
    }

    const handleImageLoad = (event) => {
      console.log('环状图加载成功：', event.target.src)
    }

    const handleImageError = (event) => {
      console.error('环状图加载失败：', event)
      console.log('尝试加载的URL：', event.target.src)
      console.log('当前分析结果：', analysisResults.value)
      ElMessage.error('环状图加载失败，请检查分析结果')
    }

    return {
      analysisResults,
      uploadUrl,
      headers,
      uploading,
      uploadProgress,
      progressText,
      uploadedFile,
      analyzing,
      loading,
      startAnalysis,
      formatNumber,
      formatPercent,
      getGCColor,
      getCodingDensityColor,
      getDownloadFiles,
      downloadFile,
      handleUploadSuccess,
      handleUploadError,
      beforeUpload,
      handleProgress,
      progressFormat,
      parameters,
      analysisTypeOptions,
      translationTableOptions,
      cellWallTypeOptions,
      sequenceTypeOptions,
      topologyOptions,
      handleProdigalFileChange,
      addRepliconSequence,
      removeRepliconSequence,
      getTotalFeatures,
      formatDuration,
      annotationTableData,
      getFileDescription,
      handleTableError,
      formatDbxrefs,
      pageSize,
      currentPage,
      searchQuery,
      filteredTableData,
      totalFilteredData,
      paginationInfo,
      handleCurrentPageChange,
      handlePageSizeChange,
      handleSearch,
      getDbxrefLink,
      getPlotMessage,
      handleImageLoad,
      handleImageError,
      currentJobId
    }
  }
}
</script>

<style scoped>
.bakta-container {
  padding: 20px;
}

.content-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
}

h1 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.description {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.description h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.description p {
  color: #666;
  line-height: 1.6;
}

.features {
  margin-top: 20px;
}

.features h3 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.2rem;
}

.features ul {
  list-style-type: none;
  padding-left: 0;
}

.features li {
  color: #666;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.features li::before {
  content: "•";
  color: #4169E1;
  position: absolute;
  left: 0;
}

.upload-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.upload-section h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.upload-info {
  color: #666;
  margin-bottom: 20px;
}

.progress-section {
  margin-top: 20px;
}

.progress-text {
  text-align: center;
  color: #666;
  margin-top: 10px;
}

.results-section {
  margin-top: 30px;
}

.results-header {
  margin-bottom: 20px;
}

.results-header h2 {
  color: #2c3e50;
  font-size: 1.5rem;
  margin: 0;
}

.results-tabs {
  margin-top: 20px;
}

.statistics-section {
  padding: 20px;
}

.stat-group {
  margin-bottom: 30px;
}

.stat-group h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.2rem;
  border-bottom: 2px solid #eee;
  padding-bottom: 8px;
}

.circular-plot {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
}

.plot-container {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.genome-plot {
  width: 100%;
  height: auto;
  max-width: 800px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.no-plot-message,
.error-message {
  color: #909399;
  font-size: 16px;
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 4px;
  width: 100%;
}

.error-message {
  color: #f56c6c;
  background: #fef0f0;
}

.annotation-table {
  padding: 20px;
  overflow: hidden; /* 防止表格溢出 */
}

.downloads-section {
  padding: 20px;
  overflow: hidden;
}

:deep(.el-table) {
  --el-table-border-color: #EBEEF5;
  --el-table-header-background-color: #F5F7FA;
}

:deep(.el-table__header) {
  font-weight: 600;
}

:deep(.el-table--border) {
  border-radius: 4px;
}

:deep(.el-table--enable-row-hover .el-table__body tr:hover > td) {
  background-color: #F5F7FA;
}

.citation-section {
  margin-top: 40px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.citation-section h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.3rem;
}

.citation-content {
  color: #666;
  line-height: 1.6;
}

.citation-box {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  margin: 10px 0;
}

.citation-box a {
  color: #409EFF;
  text-decoration: none;
}

.citation-box a:hover {
  text-decoration: underline;
}

.license-info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e4e7ed;
}

.license-text {
  margin-top: 10px;
  font-style: italic;
}

.usage-note {
  margin-top: 10px;
  font-size: 0.9em;
  color: #909399;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 200px;
}

:deep(.el-upload__tip) {
  color: #666;
}

:deep(.el-progress-bar__inner) {
  transition: width 0.3s ease;
}

.feature-list {
  list-style-type: none;
  padding-left: 20px;
  margin: 15px 0;
}

.feature-list li {
  margin-bottom: 10px;
  position: relative;
  padding-left: 20px;
}

.feature-list li::before {
  content: "•";
  color: #4169E1;
  position: absolute;
  left: 0;
}

.analysis-button-section {
  margin-top: 20px;
  text-align: center;
}

.analysis-button-section .el-button {
  padding: 12px 24px;
  font-size: 16px;
}

.parameters-section {
  margin-top: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.parameters-section h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.2rem;
}

.replicon-controls {
  margin-bottom: 20px;
  text-align: right;
}

.replicon-controls .el-button {
  padding: 8px 16px;
}

.replicon-sequence {
  margin-bottom: 10px;
}

.replicon-sequence .el-form {
  margin-bottom: 10px;
}

.replicon-sequence .el-form-item {
  margin-bottom: 5px;
}

.replicon-sequence .el-form-item label {
  font-weight: 500;
}

.replicon-sequence .el-form-item .el-input,
.replicon-sequence .el-form-item .el-select {
  width: 100%;
}

.replicon-sequence .el-button {
  margin-top: 5px;
}

.parameter-note {
  color: #606266;
  font-size: 14px;
  margin-bottom: 15px;
  padding: 8px;
  background-color: #f4f4f5;
  border-radius: 4px;
}

.parameter-tip {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}

.table-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0 1rem;
}

.entries-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.entries-control .el-select {
  width: 80px;
}

.search-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-control .el-input {
  width: 200px;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  margin-top: 10px;
}

.entries-info {
  color: #606266;
  font-size: 14px;
}

.el-pagination {
  margin-top: 0;
  justify-content: flex-end;
}

.el-pagination .el-select .el-input {
  width: 110px;
}

.el-pagination .el-pagination__sizes {
  margin-right: 15px;
}

.dbxref-link {
  margin-right: 8px;
  font-size: 0.9rem;
}

.dbxref-link:last-child {
  margin-right: 0;
}

:deep(.el-table .cell) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.el-pagination) {
  justify-content: flex-end;
}
</style> 